import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CardContent, CardMedia, Chip, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { IconSearch, IconPlus, IconBox, IconUpload } from '@tabler/icons';
import debounce from 'lodash.debounce';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import ImportDialog from '../../../../../ui-component/extended/ImportDialog';
import PaginationBar from '../../../../../ui-component/extended/PaginationBar';
import LoadingBox from '../../../../../ui-component/extended/LoadingBox';
import useRequestGuard from '../../../../../hooks/useRequestGuard';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:9000/api/';

const ViewAllProducts = () => {
    const [products, setProducts] = useState([]);
    const [appId, setAppId] = useState(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ count: 0, numPages: 1 });
    const [loading, setLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const history = useHistory();
    const { start, isCurrent } = useRequestGuard();

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarProdutos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, page, debouncedSearch]);

    const listarProdutos = async () => {
        const requestId = start();
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', page);
            if (debouncedSearch) params.set('search', debouncedSearch);
            const res = await api.get(`v1/apps/${appId}/products/?${params.toString()}`);
            if (!isCurrent(requestId)) return;
            setProducts(res.data.products || []);
            setPagination({ count: res.data.count || 0, numPages: res.data.num_pages || 1 });
        } finally {
            if (isCurrent(requestId)) setLoading(false);
        }
    };

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value) => {
                setPage(1);
                setDebouncedSearch(value);
            }, 400),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSetSearch(value);
    };

    const getImageUrl = (path) => {
        if (!path) return '/sem_produto.jpg';
        return `${BACKEND_SERVER}v1/apps/${appId}/media/${path}`;
    };

    const getProductImage = (product) => {
        const img = product.variacoes?.flatMap((v) => v.imagens || [])?.find((img) => img.imagem);
        return img ? getImageUrl(img.imagem) : '/sem_produto.jpg';
    };

    const totalEstoque = (product) => (product.variacoes || []).reduce((acc, v) => acc + (v.estoque || 0), 0);

    return (
        <Box>
            <PageHeader
                icon={IconBox}
                gradient="blue"
                title="Produtos"
                subtitle="Gerencie seu catálogo de produtos"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            placeholder="Buscar produtos..."
                            value={search}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/config/new/product')}
                        >
                            Novo Produto
                        </Button>
                        <Button variant="outlined" startIcon={<IconUpload size={18} />} onClick={() => setImportDialogOpen(true)}>
                            Importar
                        </Button>
                    </Stack>
                }
            />

            {loading && <LoadingBox label="Carregando produtos..." />}

            {!loading && (
            <Grid container spacing={3}>
                {products.map((product) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                        <GlassCard
                            sx={{ cursor: 'pointer', height: '100%' }}
                            onClick={() => history.push(`/dashboard/config/product/${product.id}`)}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <CardMedia component="img" height="180" image={getProductImage(product)} sx={{ objectFit: 'cover' }} />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '55%',
                                        background: 'linear-gradient(transparent, rgba(0,0,0,0.6))'
                                    }}
                                />
                                <Typography sx={{ position: 'absolute', bottom: 10, left: 12, color: '#fff', fontWeight: 700, fontSize: 16 }}>
                                    R$ {product.variacoes?.[0]?.valor_venda ?? '—'}
                                </Typography>
                                {!product.ativo && (
                                    <Chip
                                        label="Inativo"
                                        size="small"
                                        color="default"
                                        sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 700 }}
                                    />
                                )}
                            </Box>

                            <CardContent sx={{ p: 2.5 }}>
                                <Typography
                                    fontWeight={600}
                                    sx={{ fontSize: 15, mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {product.nome}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {[product.brand_name, product.category_name].filter(Boolean).join(' · ') || 'Sem marca/categoria'}
                                </Typography>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Chip label={`${product.variacoes?.length || 0} variações`} size="small" sx={{ fontWeight: 500 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Estoque: {totalEstoque(product)}
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </GlassCard>
                    </Grid>
                ))}
            </Grid>
            )}

            {!loading && products.length === 0 && (
                <Box mt={10} textAlign="center" color="text.secondary">
                    <Typography variant="h6">Nenhum produto encontrado</Typography>
                    <Typography variant="body2">Tente buscar outro termo ou criar um novo produto</Typography>
                </Box>
            )}

            <PaginationBar page={page} numPages={pagination.numPages} count={pagination.count} onChange={setPage} />

            <ImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                title="Importar Produtos"
                entityLabel="produtos"
                systemFields={[
                    { key: 'nome', label: 'Nome do Produto', required: true },
                    { key: 'categoria', label: 'Categoria', required: false },
                    { key: 'marca', label: 'Marca', required: false },
                    { key: 'modelo', label: 'Modelo', required: false },
                    { key: 'unidade_medida', label: 'Unidade de Medida (código)', required: false },
                    { key: 'nome_variacao', label: 'Nome da Variação (opcional - se vazio, usa "Padrão")', required: false },
                    { key: 'sku', label: 'SKU', required: false },
                    { key: 'barcode', label: 'Código de Barras', required: false },
                    { key: 'valor_venda', label: 'Valor de Venda (Preço)', required: true },
                    { key: 'valor_custo', label: 'Valor de Custo (opcional - se vazio, usa 0)', required: false },
                    { key: 'controle_estoque', label: 'Controle de Estoque (none/lot/serial)', required: false }
                ]}
                fieldsEndpoint={`v1/apps/${appId}/product_fields/`}
                importUrl={`v1/apps/${appId}/products/import/`}
                onImported={listarProdutos}
            />
        </Box>
    );
};

export default ViewAllProducts;
